import { Component, computed, effect, inject, input, OnChanges, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { FilesManagerPageStore } from './files-manager.store';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { FilterBuilder } from '../../../helper/filter.builder';
import { FilterInput } from '@it2go/data-grid';
import { FormBuilder } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { CategoryGridItemObject, FileGridItemObject, PersonGridItemObject } from '@it2go/types';
import { DynamicDialogRef } from '@openng/optimus-ui/dynamicdialog';
import { FileStatsType } from '@libs/types/src/file-manager/enum/file-stats-type.enum';
import { DefaultCategoryTypeEnum } from '@libs/types/src/file-manager/types/default-category.type';
import { FileStatsDialogComponent } from '../dialogs/file-stats-dialog/file-stats-dialog.component';
import { FileMetadataDialogComponent } from '../dialogs/file-metadata-dialog/file-metadata-dialog.component';
import { FileShareDialogComponent } from '../dialogs/file-share-dialog/file-share-dialog.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ExternalType } from '@libs/types/src/file-manager/enum/external-type.enum';
import { TableColumn } from '../../../../shared/components/kit/table/table.types';
import { FilesManagerPageStore2 } from './files-manager.store2';
import { userChipField, userClientChipColumn } from '../../../../shared/components/kit/table/table.fields';
import { router } from '../../../../shared/shared.global-service';
import { contentGlobalSuccess } from '../../../content.error';
import { folderUri } from '../../../../../utils/router.utils';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import {
    FileUploadBoxComponent,
    FileUploadMetadata,
} from '../../../../../components/kit/file-upload-box/file-upload-box.component';
import { downloadFile } from '../../../../../utils/files.utils';
import { FolderComponent } from '../../common/folder/folder.component';
import { Card } from '@openng/optimus-ui/card';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { LocalDatePipe } from '@libs/shared/pipes/local-date.pipe';
import { FormatSizePipe } from '@libs/shared/pipes/format-size.pipe';
import { DecimalPipe } from '@angular/common';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import { UserChipRenderTypeEnum } from '../../../../../components/kit/user-chip/user-chip.component.types';

@Component({
    selector: 'app-files-manager',
    templateUrl: './files-manager.component.html',
    styleUrl: './files-manager.component.scss',
    providers: [FilesManagerPageStore, FilesManagerPageStore2, BaseDialogService],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FolderComponent,
        Card,
        TranslatePipe,
        FileUploadBoxComponent,
        TableComponent,
        PrimeTemplate,
        ButtonComponent,
        LocalDatePipe,
        FormatSizePipe,
        DecimalPipe,
    ],
})
export class FilesManagerComponent extends SubscriptionComponent implements OnInit, OnChanges {

    public readonly store2 = inject(FilesManagerPageStore2);
    protected readonly folderStore = inject(FolderGlobalStore);
    protected readonly store = inject(FilesManagerPageStore);
    private readonly fb = inject(FormBuilder);
    private readonly http = inject(HttpClient);
    private readonly dialogService = inject(BaseDialogService);
    private readonly translate = inject(TranslateService);


    public folderId = input<number | null>(null);
    @ViewChild(TableComponent) table!: TableComponent;
    @ViewChild('files') files!: FileUploadBoxComponent;

    ref: DynamicDialogRef | undefined;
    columns: TableColumn[] = [];

    protected selectableCategories = computed(() => {
        const categories = this.store.data.getCategories()?.items;
        const selectedCategoryIds = this.selectedCategoryIds();
        if (!categories) {
            return [];
        }

        return categories
            .filter((category: CategoryGridItemObject) => category.code !== DefaultCategoryTypeEnum.DEFAULT && !!category.filesCount)
            .map((category: CategoryGridItemObject) => ({
                ...category,
                selected: selectedCategoryIds.includes(category.id),
            }));
    });

    protected additionalFilter = computed<FilterInput[][]>(() => {
        const filter = new FilterBuilder();

        if (this.store2.globalGlobalStore.organizationStore.serviceInContextIds().length > 0) {
            filter.where('serviceId', this.store2.globalGlobalStore.organizationStore.serviceInContextIds());
        }
        if (this.folderId()) {
            filter.folderId(this.folderId()!);
        }
        if (this.selectedCategoryIds()?.length > 0) {
            filter.where('category', this.selectedCategoryIds());
        }

        return filter.filter().filter || [];
    });
    protected selectedCategoryIds = signal<number[]>([]);

    constructor() {
        super();

        effect(() => {
            this.fetchCategories();
        });

        this.store.doOnActions(
            () => {
                this.table.reload();
                this.fetchCategories();
            },
            'deleteFile',
            'confirmFileUpload',
        );
    }

    public ngOnInit(): void {
        this.ngOnChanges();
    }

    public ngOnChanges(): void {
        this.columns = [
            { name: 'download' },
            { name: 'share' },
            { name: 'name', sortable: true, filterable: true },
            { name: 'categoryName', filterable: true },
            { name: 'kind' },
            { name: 'date', sortable: true },
            { name: 'size' },
            userChipField('uploadedBy', UserChipRenderTypeEnum.User, { filterable: false }),
            { name: 'accessCount' },
        ];
        if (!this.folderId()) {
            this.columns.push(
                {
                    ...userClientChipColumn,
                    name: 'client',
                    dataKey: 'folder.person',
                    data: (person: PersonGridItemObject) => {
                        return {
                            ...person,
                            birthDate: person.birthdate,
                        };
                    },
                    commandCallback: (item: FileGridItemObject) => this.goToFolderDetail(item),
                    filterable: false,
                },
            );
        }
        this.columns.push({ name: 'note' });
    }

    public async download(event: FileGridItemObject): Promise<void> {
        const downloadObject = await this.store.api.downloadWithStats({
            id: event.id,
            path: event.path,
            statsType: FileStatsType.user,
        });

        downloadFile(this.http, downloadObject.url, event.name || this.getName(event.path));
    }

    public async onDelete(event: FileGridItemObject): Promise<void> {
        this.store.api.deleteFile({ path: event.path, id: event.id, trash: true });
    }

    protected onCategory(selected: boolean, categoryId: number): void {
        const currentSelectedIds = this.selectedCategoryIds();

        if (selected) {
            this.selectedCategoryIds.set([...currentSelectedIds, categoryId]);
        } else {
            this.selectedCategoryIds.set(currentSelectedIds.filter(id => id !== categoryId));
        }
    }

    protected onSelected(_event: any): void {
        this.ref = this.dialogService.open(FileMetadataDialogComponent, {
            header: this.translate.instant('files.dialog.title'),
            data: { folderId: this.folderId() },
            baseZIndex: 1050,
        });

        this.ref.onClose.subscribe(async (data: any) => {
            if (data) {
                const metadata = <FileUploadMetadata>{
                    categoryId: data.categoryId,
                    note: data.note,
                    date: data.date,
                    kind: data.kind,
                    name: data.name,
                    accesses: data.accesses || [],
                };
                if (this.folderId()) {
                    metadata.accesses!.push({ externalType: ExternalType.folder, externalId: String(this.folderId()) });
                    const serviceId = this.folderStore.folder()?.serviceId;
                    if (serviceId) {
                        metadata.accesses!.push({ externalType: ExternalType.service, externalId: String(serviceId) });
                    }
                }
                const files = await this.files.uploadFiles(metadata);
                await this.files.confirmFiles(files);
                contentGlobalSuccess.next('common.saved');
            } else {
                this.files.upload.clear();
            }
        });
    }

    protected onShare(event: PointerEvent, item: FileGridItemObject) {
        event.stopPropagation();
        this.ref = this.dialogService.open(FileShareDialogComponent, {
            header: this.translate.instant('files.share.label'),
            data: { file: item },
            styleClass: 'p-dialog-width-60',
        });
    }

    protected onDownloadCount(event: PointerEvent, item: FileGridItemObject): void {
        event.stopPropagation();
        this.ref = this.dialogService.open(FileStatsDialogComponent, { data: { stats: item.stats } });
    }

    private getName(path: string): string {
        return path.split('/').pop() || 'file';
    }

    private goToFolderDetail(item: FileGridItemObject) {
        item.folderId && router.navigateByUrl(folderUri(item.folderId, item.folder?.serviceId)).then();
    }

    private fetchCategories(): void {
        const filter = new FilterBuilder().limit(999);
        filter.where('active', true);
        if (this.store2.globalGlobalStore.organizationStore.serviceInContextIds().length > 0) {
            filter.where('serviceId', this.store2.globalGlobalStore.organizationStore.serviceInContextIds());
        }
        if (this.folderId()) {
            filter.folderId(this.folderId()!);
        }

        this.store.api.getCategories(filter.create());
    }

    protected readonly DefaultCategoryTypeEnum = DefaultCategoryTypeEnum;
}
